import type {
  ChildApplyEntity,
  ChildApplyPropsPub,
  ChildApplyPropsPvt,
} from './entity/childApplyEntity';
import type { ChildApplyIRepository } from './entity/childApplyIRepository';
import type { ChildRemoveIRepository } from './entity/childRemoveIRepository';
import type { FacilityIRepository } from './entity/facilityIRepository';
import type { HomepageSettingsIRepository } from './entity/homepageSettingsIRepository';
import type { WagakoNurseryIApi } from '@/infra/wagakoNurseryIApi';
import type { ChildApplyJsonPub, ChildApplyJsonPvt } from '@/types/domain/childApply/entities/childApplyEntity';
import type { FacilityJson } from '@/types/domain/childApply/entities/facilityEntity';
import type { HomepageSettingsJson } from '@/types/domain/childApply/entities/homepageSettingsEntity';

import { DomainServiceBase } from '@/domain/domainServiceBase';

/**
 * ChildApplyApplicationService
 */
export class ChildApplyDomainService extends DomainServiceBase {
  private wagakoNurseryApi!: WagakoNurseryIApi;

  private childApplyRepository!: ChildApplyIRepository;

  private childRemoveRepository!: ChildRemoveIRepository;

  private facilityRepository!: FacilityIRepository;

  private homepageSettingsRepository!: HomepageSettingsIRepository;

  /**
   * Injection
   * @param wagakoNurseryApi wagakoNurseryApi
   * @param childApplyRepository childApplyRepository
   * @param childRemoveRepository childRemoveRepository
   * @param facilityRepository facilityRepository
   * @param homepageSettingsRepository homepageSettingsRepository
   */
  public inject(
    wagakoNurseryApi: WagakoNurseryIApi,
    childApplyRepository: ChildApplyIRepository,
    childRemoveRepository: ChildRemoveIRepository,
    facilityRepository: FacilityIRepository,
    homepageSettingsRepository: HomepageSettingsIRepository,
  ) {
    this.wagakoNurseryApi = wagakoNurseryApi;
    this.childApplyRepository = childApplyRepository;
    this.childRemoveRepository = childRemoveRepository;
    this.facilityRepository = facilityRepository;
    this.homepageSettingsRepository = homepageSettingsRepository;
  }

  /**
   * getChildApplyList
   * @param token トークン
   * @returns リスト
   */
  async getChildApplyList(token: string | null) {
    const result = await this.childApplyRepository.getList(token);
    const { nextToken } = result;
    const items = result.items.map((v: ChildApplyEntity) => v.toJson());
    return {
      /** 次のトークン */
      nextToken,
      /** アイテムリスト */
      items,
    };
  }

  /**
   * getChildRemoveList
   * @param token トークン
   * @returns リスト
   */
  async getChildRemoveList(token: string | null) {
    const result = await this.childRemoveRepository.getList(token);
    const { nextToken } = result;
    const items = result.items.map((v) => v.toJson());
    return {
      /** 次のトークン */
      nextToken,
      /** アイテムリスト */
      items,
    };
  }

  /**
   * getFacilityList
   * @returns リスト
   */
  async getFacilityList() {
    const result = await this.facilityRepository.getAllList();
    return result;
  }

  /**
   * getFacilityHomepageSettings
   * @param nurseryId 施設ID
   * @param _token トークン
   * @returns 結果
   */
  async getFacilityHomepageSettings(
    nurseryId: string,
    _token: string | null = null,
  ) {
    const result = await this.homepageSettingsRepository.getPublicList(nurseryId, null);
    if (result.items.length > 0) {
      return result.items[0];
    }
    return undefined;
  }

  /**
   * getFacilityHomepageSettingsList
   * @param facilityList facilityList
   * @returns 結果
   */
  async getFacilityHomepageSettingsList(
    facilityList: FacilityJson[],
  ) {
    const facilityHomepageSettingsList: HomepageSettingsJson[] = [];
    for (let i = 0; i < facilityList.length; i += 1) {
      const nurseryId = facilityList[i].data.facilityId;
      const entity = await this.getFacilityHomepageSettings(nurseryId);
      if (entity) {
        facilityHomepageSettingsList.push(entity.toJsonData());
      }
    }
    return facilityHomepageSettingsList;
  }

  /**
   * sendChildApply
   * @param entity entity
   */
  async sendChildApply(
    entity: ChildApplyEntity,
  ) {
    // Facilityに申請する
    await this.wagakoNurseryApi.send<
      ChildApplyPropsPvt,
      ChildApplyJsonPvt,
      ChildApplyPropsPub,
      ChildApplyJsonPub
    >(entity);
    // AppのEntityに登録する
    // const result: ChildApplyEntity = await share.childApply.create(entity);
  }
}
